import ReactDOM from "react-dom"
import React, { ReactElement } from 'react'
import ErrorBoundary from "./ErrorBoundary"

const PREFECTURES = [
  {id: 1, name: '北海道'},
  {id: 2, name: '青森県'},
  {id: 3, name: '岩手県'},
  {id: 4, name: '宮城県'},
  {id: 5, name: '秋田県'},
  {id: 6, name: '山形県'},
  {id: 7, name: '福島県'},
  {id: 8, name: '茨城県'},
  {id: 9, name: '栃木県'},
  {id: 10, name: '群馬県'},
  {id: 11, name: '埼玉県'},
  {id: 12, name: '千葉県'},
  {id: 13, name: '東京都'},
  {id: 14, name: '神奈川県'},
  {id: 15, name: '新潟県'},
  {id: 16, name: '富山県'},
  {id: 17, name: '石川県'},
  {id: 18, name: '福井県'},
  {id: 19, name: '山梨県'},
  {id: 20, name: '長野県'},
  {id: 21, name: '岐阜県'},
  {id: 22, name: '静岡県'},
  {id: 23, name: '愛知県'},
  {id: 24, name: '三重県'},
  {id: 25, name: '滋賀県'},
  {id: 26, name: '京都府'},
  {id: 27, name: '大阪府'},
  {id: 28, name: '兵庫県'},
  {id: 29, name: '奈良県'},
  {id: 30, name: '和歌山県'},
  {id: 31, name: '鳥取県'},
  {id: 32, name: '島根県'},
  {id: 33, name: '岡山県'},
  {id: 34, name: '広島県'},
  {id: 35, name: '山口県'},
  {id: 36, name: '徳島県'},
  {id: 37, name: '香川県'},
  {id: 38, name: '愛媛県'},
  {id: 39, name: '高知県'},
  {id: 40, name: '福岡県'},
  {id: 41, name: '佐賀県'},
  {id: 42, name: '長崎県'},
  {id: 43, name: '熊本県'},
  {id: 44, name: '大分県'},
  {id: 45, name: '宮崎県'},
  {id: 46, name: '鹿児島県'},
  {id: 47, name: '沖縄県'}
]

export function ready(fn: () => void) {
  if (document.readyState != 'loading'){
    fn()
  } else {
    document.addEventListener('DOMContentLoaded', fn)
  }
}

export function metaContent(name: string): string | null {
  const metaElem = document.getElementsByName(name)[0]
  if (metaElem) {
    const metaContent = metaElem.getAttribute('content')
    return metaContent
  }
  return null
}

export function getUserDevice() {
  const ua = navigator.userAgent
  if (ua.indexOf('iPhone') > -1 || (ua.indexOf('Android') > -1 && ua.indexOf('Mobile') > -1)) {
    return 'smartphone'
  } else if (ua.indexOf('iPad') > -1 || ua.indexOf('Android') > -1) {
    return 'tablet'
  }
  return 'pc'
}

export type RenderAtMountpointComponentFactory =
  (mountpoint: HTMLElement) => (ReactElement | Promise<ReactElement>)

const _renderAtMountpoint = async (mountpoint: HTMLElement, factory: RenderAtMountpointComponentFactory) => {
  const component = await Promise.resolve(factory(mountpoint))
  ReactDOM.render(
    React.createElement(
      ErrorBoundary,
      {},
      component
    ),
    mountpoint
  )
}

export async function renderAtMountpoint(mountpointId: string, componentFactory: RenderAtMountpointComponentFactory) {
  const mountpoint = document.getElementById(mountpointId)
  if (mountpoint) {
    await _renderAtMountpoint(mountpoint, componentFactory)
  }
}

export function renderAtAllMountpoints(mountpointSelector: string, componentFactory: RenderAtMountpointComponentFactory): Promise<void[]> {
  const mountpoints = Array.from(document.querySelectorAll<HTMLElement>(mountpointSelector))
  const promises = mountpoints.map((mountpoint) => {
    return _renderAtMountpoint(mountpoint, componentFactory)
  })

  return Promise.all(promises)
}

export function tlog(tag: string, ...args: any[]): void {
  console.log(
    `%c${tag}`,
    "background-color: #333; color: #EEE; padding: 1px 4px;",
    ...args
  )
}

export async function timeout(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms))
}

export function formatRequestDuration(duration: number): string {
  const d = Math.ceil(duration)
  const hours = Math.floor(d / (60 * 60))
  const minutes = Math.floor(d / 60) % 60
  let out = ""
  if (hours > 0) {
    out += `${hours}時間`
  }
  out += `${minutes}分`

  const seconds = d - (hours * 60 * 60) - (minutes * 60)
  out += `${seconds}秒`

  return out
}

export function isSupportedBrowser(): boolean {
  const ua: string = navigator.userAgent
  if (
    ua.indexOf('Trident') !== -1 ||  // IE11
    ua.indexOf('MSIE') !== -1 ||  // IE before 11
    ua.indexOf('FxiOS') !== -1 // iOS Firefox
  ) {
    return false
  }
  return true
}

export function sortPrefectures(prefectures: string[]): string[] {
  return PREFECTURES.filter( p => prefectures.indexOf(p.name) !== -1).map(p => p.name)
}

export function shuffleArray(array: any[]): any[] {
  for (let i = array.length - 1; i >= 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
  return array;
}

export function range(start: number, end: number){
  return [...Array(end - start)].map((_, i) => start + i)
}

export function isMobileDevice(): boolean {
  const ua: string = navigator.userAgent
  if (ua.indexOf("mobile") !== -1 && /Android|iPhone|iPad|iPod/i.test(ua)) {
    return true
  }
  return false
}

export function debounce(fn: ()=> void, interval: number, args?: any[]) {
  let timerId: NodeJS.Timeout
  return () => {
    clearTimeout(timerId)
    const context = this
    timerId = setTimeout(() => {
      fn.apply(context, args)
    }, interval);
  }
}
